import { z } from "zod";
import { localizedTextSchema } from "./intake-template";
import { wellnessDimensionSchema } from "./wellness";

// TES-294 (J12) — the per-patient, VERSIONED Wellness Program. This is the real
// per-patient care plan, distinct from the per-tenant `WellnessFocus` global
// content (which is admin-authored template copy shared by every patient).
//
// Lifecycle: a program is authored as a `draft` (editing surfaces are J8/J9),
// then `activate`d. Activating a new version SUPERSEDES the patient's currently
// active program (old → `replaced`, stamped with `replacedByVersion` +
// `supersededAt`), so exactly one program is active per patient at a time —
// this is the persistence backbone J14 (persistent_until_replaced) reads.
//
// All patient-facing text is LocalizedText {sv,en} (TES-331 — no Swedish-only).

// ---------------------------------------------------------------------------
// Status + transition table
// ---------------------------------------------------------------------------

export const WELLNESS_PROGRAM_STATUSES = [
  "draft",
  "active",
  "replaced",
  "archived",
] as const;
export const wellnessProgramStatusSchema = z.enum(WELLNESS_PROGRAM_STATUSES);
export type WellnessProgramStatus = z.infer<typeof wellnessProgramStatusSchema>;

// draft → active (the activation, which supersedes the prior active program).
// active → replaced (superseded by a newer version) | archived (retired).
// A draft that is never activated can be archived (discarded). replaced +
// archived are terminal.
export const WELLNESS_PROGRAM_TRANSITIONS: Record<
  WellnessProgramStatus,
  readonly WellnessProgramStatus[]
> = {
  draft: ["active", "archived"],
  active: ["replaced", "archived"],
  replaced: [],
  archived: [],
};

export function canTransitionWellnessProgram(
  from: WellnessProgramStatus,
  to: WellnessProgramStatus,
): boolean {
  return WELLNESS_PROGRAM_TRANSITIONS[from].includes(to);
}

// ---------------------------------------------------------------------------
// Provenance — what drove this version of the program.
// ---------------------------------------------------------------------------

export const WELLNESS_BASED_ON = [
  "health_profile",
  "check_in",
  "doctor_restriction",
  "coach_decision",
] as const;
export const wellnessBasedOnSchema = z.enum(WELLNESS_BASED_ON);
export type WellnessBasedOn = z.infer<typeof wellnessBasedOnSchema>;

// ---------------------------------------------------------------------------
// Module + restriction + substitution content (all patient-facing = localized)
// ---------------------------------------------------------------------------

// Both locales required + non-empty: a program a patient sees must never render
// a blank module (mirrors wellness.ts `nonEmptyLocalizedSchema`).
const nonEmptyLocalizedSchema = z.object({
  sv: z.string().min(1),
  en: z.string().min(1),
});

// One module in the program — a dimension (nutrition | movement | mind) with a
// title, a body, and a flat list of bullet items. `items` are localized so the
// patient never sees a Swedish-only bullet (TES-331).
export const wellnessModuleSchema = z.object({
  dimension: wellnessDimensionSchema,
  title: nonEmptyLocalizedSchema,
  body: nonEmptyLocalizedSchema,
  items: z.array(localizedTextSchema).default([]),
});
export type WellnessModule = z.infer<typeof wellnessModuleSchema>;

// A restriction the program must respect (e.g. a doctor-set movement limit).
// `source` records where it came from so the coach can see doctor-imposed
// restrictions separately from coach-chosen ones.
export const wellnessRestrictionSchema = z.object({
  label: nonEmptyLocalizedSchema,
  source: wellnessBasedOnSchema.optional(),
});
export type WellnessRestriction = z.infer<typeof wellnessRestrictionSchema>;

// A "swap X for Y" substitution (e.g. replace a running block with cycling).
export const wellnessSubstitutionSchema = z.object({
  from: nonEmptyLocalizedSchema,
  to: nonEmptyLocalizedSchema,
});
export type WellnessSubstitution = z.infer<typeof wellnessSubstitutionSchema>;

// ---------------------------------------------------------------------------
// Create payload (authoring) — always lands as a `draft`; version + status are
// server-assigned, never client-supplied.
// ---------------------------------------------------------------------------

export const createWellnessProgramSchema = z.object({
  basedOn: wellnessBasedOnSchema,
  // Patient-facing goal statement (localized). Internal-only justification is
  // `rationale` below.
  patientGoal: localizedTextSchema.nullable().optional(),
  // Coach's internal justification for this version — NOT patient-facing, so a
  // plain string (mirrors the flow-assignment `reason`).
  rationale: z.string().max(2000).nullable().optional(),
  modules: z.array(wellnessModuleSchema).default([]),
  restrictions: z.array(wellnessRestrictionSchema).default([]),
  substitutions: z.array(wellnessSubstitutionSchema).default([]),
  // Optional scheduling window; `activate` stamps activeFrom if left unset.
  activeFrom: z.string().datetime().nullable().optional(),
  activeUntil: z.string().datetime().nullable().optional(),
});
export type CreateWellnessProgram = z.infer<typeof createWellnessProgramSchema>;

// ---------------------------------------------------------------------------
// DTO — the full stored program as returned by the employee-api.
// ---------------------------------------------------------------------------

export const wellnessProgramDtoSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  version: z.number().int().positive(),
  status: wellnessProgramStatusSchema,
  basedOn: wellnessBasedOnSchema,
  createdByEmployeeId: z.string().nullable(),
  patientGoal: localizedTextSchema.nullable(),
  rationale: z.string().nullable(),
  modules: z.array(wellnessModuleSchema),
  restrictions: z.array(wellnessRestrictionSchema),
  substitutions: z.array(wellnessSubstitutionSchema),
  activeFrom: z.string().nullable(),
  activeUntil: z.string().nullable(),
  replacedByVersion: z.number().int().nullable(),
  supersededAt: z.string().nullable(),
  // TES-294 §4 — behavioral-signal recommendation linkage (MVP hook): a note
  // that a fired signal (e.g. a repeated too-heavy workout) recommends the
  // coach adjust this program.
  recommendationNote: z.string().nullable(),
  recommendedAt: z.string().nullable(),
  recommendationSignalKey: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WellnessProgramDto = z.infer<typeof wellnessProgramDtoSchema>;
